import React, { useEffect, useState } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../Config/firebase";
import { LOGO_MARCA_DAGUA, LOGO_RELATORIO_PADRAO } from "../../Config/branding";

const LOGO_PADRAO = LOGO_RELATORIO_PADRAO;
const cachePorTenant = new Map();
const cacheMarcaDaguaPorTenant = new Map();

function chaveLogoTenant(tenantId) {
  return `logoSistemaUrl:${tenantId}`;
}

function chaveCacheLogo(tenantId, variante) {
  return `${tenantId}:${variante}`;
}

function obterLogoLocal(tenantId) {
  if (!tenantId) return "";
  return localStorage.getItem(chaveLogoTenant(tenantId)) || "";
}

function salvarLogoLocal(tenantId, logoUrl) {
  if (!tenantId) return;
  if (logoUrl && logoUrl !== LOGO_PADRAO) {
    localStorage.setItem(chaveLogoTenant(tenantId), logoUrl);
    localStorage.setItem("logoRelatorioUrl", logoUrl);
    return;
  }
  localStorage.removeItem(chaveLogoTenant(tenantId));
  localStorage.removeItem("logoRelatorioUrl");
}

export function limparCacheLogoRelatorio(tenantId) {
  if (!tenantId) {
    cachePorTenant.clear();
    cacheMarcaDaguaPorTenant.clear();
    return;
  }

  for (const chave of cachePorTenant.keys()) {
    if (chave.startsWith(`${tenantId}:`)) cachePorTenant.delete(chave);
  }
  cacheMarcaDaguaPorTenant.delete(tenantId);
}

export function useMarcaDaguaRelatorio() {
  const tenantId = String(localStorage.getItem("tenantId") || "").trim();
  const [marcaDaguaUrl, setMarcaDaguaUrl] = useState(
    () => cacheMarcaDaguaPorTenant.get(tenantId) || LOGO_MARCA_DAGUA
  );

  useEffect(() => {
    if (!tenantId) {
      setMarcaDaguaUrl(LOGO_MARCA_DAGUA);
      return undefined;
    }

    let cancelado = false;
    setMarcaDaguaUrl(cacheMarcaDaguaPorTenant.get(tenantId) || LOGO_MARCA_DAGUA);

    getDoc(doc(db, "tenant_branding", tenantId))
      .then((snap) => {
        if (cancelado) return;
        const url = snap.exists() ? snap.data()?.marcaDaguaUrl || LOGO_MARCA_DAGUA : LOGO_MARCA_DAGUA;
        cacheMarcaDaguaPorTenant.set(tenantId, url);
        setMarcaDaguaUrl(url);
      })
      .catch(() => {
        if (!cancelado) setMarcaDaguaUrl(LOGO_MARCA_DAGUA);
      });

    const atualizarMarcaDagua = (event) => {
      if (event.detail?.tenantId && event.detail.tenantId !== tenantId) return;
      const url = event.detail?.marcaDaguaUrl || LOGO_MARCA_DAGUA;
      cacheMarcaDaguaPorTenant.set(tenantId, url);
      setMarcaDaguaUrl(url);
    };
    window.addEventListener("tenant-marca-dagua-atualizada", atualizarMarcaDagua);

    return () => {
      cancelado = true;
      window.removeEventListener("tenant-marca-dagua-atualizada", atualizarMarcaDagua);
    };
  }, [tenantId]);

  return marcaDaguaUrl;
}

export default function LogoRelatorio({
  alt = "Logo da empresa",
  logoPadrao = LOGO_PADRAO,
  variante = "padrao",
  monocromaticaBranca = false,
  style,
  ...props
}) {
  const tenantId = String(localStorage.getItem("tenantId") || "").trim();
  const chaveCache = chaveCacheLogo(tenantId, variante);
  const [src, setSrc] = useState(
    () => cachePorTenant.get(chaveCache) || obterLogoLocal(tenantId) || LOGO_PADRAO
  );

  useEffect(() => {
    if (!tenantId) {
      setSrc(LOGO_PADRAO);
      return undefined;
    }

    let cancelado = false;

    getDoc(doc(db, "tenant_branding", tenantId))
      .then((snap) => {
        if (cancelado) return;
        const dados = snap.exists() ? snap.data() : {};
        const novaLogo =
          variante === "calibracao-prensa"
            ? dados?.logoCalibracaoPrensaUrl || dados?.logoUrl || LOGO_PADRAO
            : dados?.logoUrl || LOGO_PADRAO;
        cachePorTenant.set(chaveCache, novaLogo);
        if (variante === "padrao") salvarLogoLocal(tenantId, novaLogo);
        setSrc(novaLogo);
      })
      .catch((error) => {
        if (cancelado) return;
        console.error("Erro ao acompanhar a logo da empresa:", error);
        setSrc(cachePorTenant.get(chaveCache) || obterLogoLocal(tenantId) || LOGO_PADRAO);
      });

    const eventoLogo =
      variante === "calibracao-prensa"
        ? "tenant-logo-calibracao-prensa-atualizada"
        : "tenant-logo-atualizada";

    const atualizarLogo = (event) => {
      if (event.detail?.tenantId && event.detail.tenantId !== tenantId) return;
      const novaLogo =
        event.detail?.logoUrl ||
        (variante === "calibracao-prensa" ? obterLogoLocal(tenantId) : "") ||
        LOGO_PADRAO;
      cachePorTenant.set(chaveCache, novaLogo);
      if (variante === "padrao") salvarLogoLocal(tenantId, novaLogo);
      setSrc(novaLogo);
    };
    window.addEventListener(eventoLogo, atualizarLogo);

    return () => {
      cancelado = true;
      window.removeEventListener(eventoLogo, atualizarLogo);
    };
  }, [chaveCache, tenantId, variante]);

  return (
    <img
      {...props}
      src={src === LOGO_PADRAO ? logoPadrao : src}
      alt={alt}
      style={{
        display: "block",
        width: "auto",
        height: "auto",
        maxWidth: "100%",
        maxHeight: "60px",
        objectFit: "contain",
        margin: "0 auto",
        filter:
          monocromaticaBranca && src !== LOGO_PADRAO
            ? "brightness(0) invert(1)"
            : undefined,
        ...style,
      }}
      onError={(event) => {
        if (event.currentTarget.getAttribute("src") === logoPadrao) return;
        event.currentTarget.src = logoPadrao;
      }}
    />
  );
}
